/**
 * Chat (janela direita do Inbox split-view).
 *
 * Polling 10s com ?since=<ultimo_msg_created_at> — so pega mensagens
 * novas, nao recarrega historico inteiro. Marcar lida automatico ao
 * montar.
 *
 * Scroll automatico pro fim ao montar e ao chegar msg nova SE o usuario
 * ja estava no fim (preserva scroll de quem ta lendo historico).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiX } from "react-icons/fi";

import { useAuth } from "../../../context/AuthContext";
import useWhatsappPolling from "../../../hooks/useWhatsappPolling";
import {
  fecharConversa,
  listarMensagens,
  marcarConversaLida,
  obterConversa,
  reabrirConversa,
  STATUS_MENSAGEM_LABEL,
  STATUS_MENSAGEM_TONE,
} from "../../../services/whatsapp";
import AsyncButton from "../../../components/AsyncButton";
import { ErrorMsg, Pill } from "../../Orcamentos/styles";
import {
  Avatar,
  Bubble,
  BubbleRow,
  ChatEmpty,
  ChatHeader,
  ChatMessages,
  ChatPane,
} from "../styles";
import Composer from "./Composer";

function avatarLetras(nome, wa_number) {
  const fonte = (nome || wa_number || "?").trim();
  const partes = fonte.split(/\s+/).filter(Boolean);
  if (partes.length === 0) return "?";
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
}

function fmtHora(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

export default function Chat({ conversaId, conversaResumo, onAtualizarLista }) {
  const { request } = useAuth();

  const [conversa, setConversa] = useState(conversaResumo || null);
  const [mensagens, setMensagens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const scrollRef = useRef(null);
  const ultimaMsgIdRef = useRef(null);
  const ultimaCreatedAtRef = useRef(null);

  // Carrega historico inicial + carrega conversa
  const carregarInicial = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [conv, payloadMsgs] = await Promise.all([
        obterConversa(request, conversaId),
        listarMensagens(request, conversaId, { limit: 100 }),
      ]);
      setConversa(conv);
      const lista = Array.isArray(payloadMsgs)
        ? payloadMsgs
        : payloadMsgs?.results || [];
      // Ordena por created_at asc (mais antigo primeiro pra renderizar de cima pra baixo)
      lista.sort((a, b) => {
        const ta = new Date(a.created_at || 0).getTime();
        const tb = new Date(b.created_at || 0).getTime();
        return ta - tb;
      });
      setMensagens(lista);
      if (lista.length > 0) {
        const ultima = lista[lista.length - 1];
        ultimaMsgIdRef.current = ultima.id;
        ultimaCreatedAtRef.current = ultima.created_at;
      }
      // Marca conversa como lida (zera contador)
      try {
        await marcarConversaLida(request, conversaId);
        if (onAtualizarLista) onAtualizarLista();
      } catch (_e) {
        // silencioso
      }
    } catch (err) {
      setError(err?.message || "Erro ao carregar mensagens.");
    } finally {
      setLoading(false);
    }
  }, [request, conversaId, onAtualizarLista]);

  // Polling de mensagens novas (apenas since)
  const pollMensagens = useCallback(async () => {
    if (!conversaId) return;
    try {
      const payload = await listarMensagens(request, conversaId, {
        since: ultimaCreatedAtRef.current || undefined,
        limit: 50,
      });
      const lista = Array.isArray(payload) ? payload : payload?.results || [];
      if (lista.length === 0) return;
      // Deduplica via id (alguns backends retornam a propria ultima mensagem do since)
      const idsAtuais = new Set(mensagens.map((m) => m.id));
      const novas = lista.filter((m) => !idsAtuais.has(m.id));
      if (novas.length === 0) return;
      novas.sort((a, b) => {
        const ta = new Date(a.created_at || 0).getTime();
        const tb = new Date(b.created_at || 0).getTime();
        return ta - tb;
      });

      // Preserva scroll: so anda pro fim se ja estava no fim
      const el = scrollRef.current;
      const estavaNoFim =
        el && Math.abs(el.scrollHeight - el.scrollTop - el.clientHeight) < 80;

      setMensagens((prev) => [...prev, ...novas]);
      const ultima = novas[novas.length - 1];
      ultimaCreatedAtRef.current = ultima.created_at;

      // Marca como lida toda vez que chega inbound enquanto conversa aberta
      const temInbound = novas.some((m) => m.direction === "INBOUND");
      if (temInbound) {
        try {
          await marcarConversaLida(request, conversaId);
          if (onAtualizarLista) onAtualizarLista();
        } catch (_e) {
          // silencioso
        }
      }

      // Scroll pro fim apos render
      if (estavaNoFim) {
        setTimeout(() => {
          if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
          }
        }, 50);
      }
    } catch (_err) {
      // silencioso
    }
  }, [request, conversaId, mensagens, onAtualizarLista]);

  useEffect(() => {
    if (conversaId) carregarInicial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversaId]);

  // Scroll pro fim na primeira carga
  const temMensagens = mensagens.length > 0;
  useEffect(() => {
    if (temMensagens && scrollRef.current) {
      // pequeno delay para garantir paint
      const t = setTimeout(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      }, 30);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [conversaId, temMensagens]); // dispara so quando muda conversa ou popula primeiro

  useWhatsappPolling(pollMensagens, 10000, [pollMensagens]);

  const handleEnviado = (novaMsg) => {
    if (!novaMsg) return;
    setMensagens((prev) => [...prev, novaMsg]);
    ultimaCreatedAtRef.current = novaMsg.created_at;
    setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    }, 30);
    if (onAtualizarLista) onAtualizarLista();
  };

  const handleFechar = async () => {
    await fecharConversa(request, conversaId);
    await carregarInicial();
    if (onAtualizarLista) onAtualizarLista();
  };

  const handleReabrir = async () => {
    await reabrirConversa(request, conversaId);
    await carregarInicial();
    if (onAtualizarLista) onAtualizarLista();
  };

  if (loading && mensagens.length === 0) {
    return (
      <ChatPane>
        <ChatEmpty>Carregando...</ChatEmpty>
      </ChatPane>
    );
  }

  if (error) {
    return (
      <ChatPane>
        <ChatHeader>
          <div className="info">
            <h3 className="nome">Erro</h3>
          </div>
        </ChatHeader>
        <ErrorMsg style={{ margin: 12 }}>{error}</ErrorMsg>
      </ChatPane>
    );
  }

  const nomeExibido = conversa?.contact_nome || conversa?.contact_wa_number || "—";
  const wa = conversa?.contact_wa_number || "";
  const isAberta = conversa?.status === "ABERTA";

  return (
    <ChatPane>
      <ChatHeader>
        <Avatar>{avatarLetras(conversa?.contact_nome, conversa?.contact_wa_number)}</Avatar>
        <div className="info">
          <h3 className="nome">{nomeExibido}</h3>
          <div className="submeta">
            {wa && <span>{wa}</span>}
            {conversa?.instance_nome && <span>· {conversa.instance_nome}</span>}
            {conversa?.status && (
              <Pill $tone={isAberta ? "info" : "neutral"}>
                {conversa.status}
              </Pill>
            )}
          </div>
        </div>
        <div className="actions">
          {isAberta ? (
            <AsyncButton
              variant="secondary"
              onClick={handleFechar}
              successMessage="Conversa fechada"
            >
              <FiX style={{ marginRight: 4 }} /> Fechar
            </AsyncButton>
          ) : (
            <AsyncButton
              variant="secondary"
              onClick={handleReabrir}
              successMessage="Conversa reaberta"
            >
              Reabrir
            </AsyncButton>
          )}
        </div>
      </ChatHeader>

      <ChatMessages ref={scrollRef}>
        {mensagens.length === 0 && (
          <ChatEmpty>Nenhuma mensagem ainda. Comece a conversar abaixo.</ChatEmpty>
        )}
        {mensagens.map((m) => {
          const texto = m.direction === "OUTBOUND" ? m.final_text : m.raw_text;
          return (
            <BubbleRow key={m.id} $direction={m.direction}>
              <Bubble $direction={m.direction}>
                <div className="texto">{texto}</div>
                <div className="meta">
                  <span>{fmtHora(m.created_at || m.sent_at || m.received_at)}</span>
                  {m.status && (
                    <Pill $tone={STATUS_MENSAGEM_TONE[m.status] || "neutral"}>
                      {STATUS_MENSAGEM_LABEL[m.status] || m.status}
                    </Pill>
                  )}
                </div>
              </Bubble>
            </BubbleRow>
          );
        })}
      </ChatMessages>

      <Composer
        conversa={conversa}
        onEnviado={handleEnviado}
        disabled={!isAberta}
      />
    </ChatPane>
  );
}
